import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Animal } from 'shared/@generated/models';
import { falCircleInfo } from 'shared/@generated/icons/falCircleInfo';
import { falArrowRotateLeft } from 'shared/@generated/icons/falArrowRotateLeft';
import { falAngleDown } from 'shared/@generated/icons/falAngleDown';
import { falArrowRight } from 'shared/@generated/icons/falArrowRight';
import { AdvisorEntrance } from 'shared/components/breed-advisor-entrance';
import { BreedAdvisorMatch } from 'shared/components/breed-advisor-match';
import { BreedAdvisorResultGroup } from 'shared/components/breed-advisor-result-group';
import { BreedAdvisorShare } from 'shared/components/breed-advisor-share';
import { BreedAdvisorSizeInput } from 'shared/components/breed-advisor-size-input';
import { Button } from 'shared/components/button';
import { ConfirmModal } from 'shared/components/confirm-modal';
import { TypingIndicator } from 'shared/components/chat/typing-indicator';
import { BreedMultiSelect } from 'shared/components/form/breed-multi-select';
import { TextInput } from 'shared/components/form/text-input';
import { Icon } from 'shared/components/icon';
import { Text } from 'shared/components/text';
import {
  advisorMaxFreeTextLength,
  advisorMaxFavorites,
  getAdvisorCardCopy,
  type AdvisorCard,
  type AdvisorConflict,
  type AdvisorFeedbackReason,
  type AdvisorRound,
  type AdvisorTranslate,
} from 'shared/library/breed-advisor';
import { useBreedAdvisor } from 'shared/library/use-breed-advisor';
import { useAdvisorConversationMotion } from 'shared/library/use-advisor-conversation-motion';
import { useBreeds } from 'shared/state/breeds';

const feedbackReasons: AdvisorFeedbackReason[] = [
  'too_active',
  'too_large',
  'too_much_grooming',
  'too_much_shedding',
  'too_vocal',
  'appearance',
  'other',
];

function sameConflict(a: AdvisorConflict, b: AdvisorConflict) {
  return (
    a.trait === b.trait &&
    a.text === b.text &&
    a.original_value === b.original_value &&
    a.proposed_value === b.proposed_value &&
    a.original_mode === b.original_mode &&
    a.proposed_mode === b.proposed_mode
  );
}

function Reply({
  children,
  onPress,
  disabled,
  testID,
}: {
  children: string;
  onPress: () => void;
  disabled?: boolean;
  testID?: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      disabled={disabled}
      testID={testID}
      onPress={onPress}
      className="border-brand-brown-dark/15 hover:border-brand-brown-dark/30 min-h-11 max-w-full justify-center self-end rounded border bg-white px-4 py-2.5 disabled:opacity-50"
    >
      <Text className="text-right text-sm font-semibold leading-5 text-black">{children}</Text>
    </Pressable>
  );
}

function Replies({ children }: { children: ReactNode }) {
  return <View className="items-end gap-1.5">{children}</View>;
}

function Sent({ children, animated = false }: { children: string; animated?: boolean }) {
  return (
    <AdvisorEntrance enabled={animated} duration={220} className="items-end">
      <View className="bg-brand-green-dark max-w-[82%] self-end rounded-2xl rounded-br-none px-4 py-3">
        <Text className="text-sm leading-6 text-white">{children}</Text>
      </View>
    </AdvisorEntrance>
  );
}

function Question({
  text,
  explanation,
  label,
  animated = false,
}: {
  text: string;
  explanation?: string;
  label: string;
  animated?: boolean;
}) {
  return (
    <AdvisorEntrance enabled={animated} className="max-w-[82%] self-start">
      <View
        className={`bg-brand-brown-dark/5 max-w-sm self-start rounded-2xl rounded-bl-none px-4 ${explanation ? 'pb-1.5 pt-3' : 'py-3'}`}
      >
        <Text className="text-brand-brown-dark text-base font-medium leading-6">{text}</Text>
        {explanation && <Explanation text={explanation} label={label} />}
      </View>
    </AdvisorEntrance>
  );
}

function Explanation({ text, label }: { text: string; label: string }) {
  const [open, setOpen] = useState(false);
  return (
    <View className="mt-1.5">
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        aria-expanded={open}
        onPress={() => setOpen(!open)}
        className="group min-h-7 flex-row items-center gap-2 self-start py-1"
      >
        <Icon
          icon={falCircleInfo}
          className="text-brand-brown-dark/65 group-hover:text-brand-brown-dark size-3.5"
        />
        <Text className="text-brand-brown-dark/65 group-hover:text-brand-brown-dark text-xs font-medium">
          {label}
        </Text>
      </Pressable>
      {open && (
        <Text className="text-brand-brown-dark/70 pb-1.5 pt-1 text-sm leading-6">{text}</Text>
      )}
    </View>
  );
}

function Match({
  card,
  copy,
  busy,
  readOnly = false,
  reaction,
  onLike,
  onDislike,
}: {
  card: AdvisorCard;
  copy: AdvisorTranslate;
  busy: boolean;
  readOnly?: boolean;
  reaction?: 'like' | 'dislike';
  onLike: () => void;
  onDislike: () => void;
}) {
  const content = getAdvisorCardCopy(card, copy);
  return (
    <BreedAdvisorMatch
      card={card}
      copy={copy}
      warnings={
        !!content.data_warnings.length && (
          <Explanation text={content.data_warnings.join('\n\n')} label={copy('data_warning')} />
        )
      }
    >
      {!readOnly && (
        <Replies>
          <Reply disabled={busy || reaction === 'like'} onPress={onLike}>
            {reaction === 'like' ? copy('liked_confirmation') : copy('liked')}
          </Reply>
          <Reply disabled={busy || reaction === 'dislike'} onPress={onDislike}>
            {copy('disliked')}
          </Reply>
        </Replies>
      )}
    </BreedAdvisorMatch>
  );
}

export function BreedAdvisorConversation() {
  const { t: translate } = useTranslation('components');
  const copy = useCallback<AdvisorTranslate>(
    (key, values) => translate(`breed_advisor.${key}`, values),
    [translate],
  );
  const advisor = useBreedAdvisor(copy);
  return <BreedAdvisorConversationContent advisor={advisor} copy={copy} />;
}

export function BreedAdvisorConversationContent({
  advisor,
  copy,
}: {
  advisor: ReturnType<typeof useBreedAdvisor>;
  copy: AdvisorTranslate;
}) {
  const { t: translate } = useTranslation('components');
  const { breeds } = useBreeds();
  const [continueQuestions, setContinueQuestions] = useState(false);
  const [dislike, setDislike] = useState<AdvisorCard | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const scroll = useRef<ScrollView>(null);
  const pinned = useRef(true);
  const previousScrollY = useRef(0);
  const scrollSize = useRef({ content: 0, viewport: 0 });
  const scrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const roundPositions = useRef<Record<string, number>>({});
  const latestRound = advisor.rounds.at(-1);
  const lastRound = useRef<string | undefined>(undefined);
  const busy = advisor.status === 'loading';
  const coverageValues = {
    answered:
      advisor.coverage.answered +
      Number(advisor.favoriteInterest !== undefined) +
      Number(advisor.favoriteInterest === true && advisor.favoriteStage === 'done') +
      Number(!!advisor.session || advisor.rounds.length > 0),
    total: advisor.coverage.total + 2 + Number(advisor.favoriteInterest === true),
  };
  const started =
    advisor.favoriteStage === 'done' || advisor.answers.length > 0 || !!advisor.session;
  const choosing =
    started && !busy && (advisor.status === 'questions' || continueQuestions) && !!advisor.question;
  const showText = started && !busy && advisor.status === 'questions' && !advisor.question;
  const pendingConflict = advisor.session?.conflicts[0];
  function getInputMode() {
    if (!advisor.hydrated) return null;
    if (advisor.sessionExpired) return null;
    if (busy) return 'loading';
    if (advisor.error) return 'error';
    if (pendingConflict) return 'conflict';
    if (dislike) return 'dislike';
    if (showText) return 'text';
    if (choosing) return 'question';
    if (!started) return advisor.favoriteStage === 'select' ? 'favorite_select' : 'favorites';
    if (advisor.status === 'results') return 'results';
    return null;
  }
  const inputMode = getInputMode();
  const inputKey = `${inputMode}:${advisor.favoriteStage}:${advisor.question?.id ?? ''}:${pendingConflict?.trait ?? ''}:${dislike?.breed.id ?? ''}`;
  const motion = useAdvisorConversationMotion({
    inputKey,
    enabled:
      !!inputMode &&
      ['favorites', 'favorite_select', 'question', 'text', 'conflict', 'dislike'].includes(
        inputMode,
      ),
    scope: `${advisor.storageKey}:${advisor.activeHistoryId ?? 'current'}`,
  });
  const showInput = motion.stage !== 'typing';
  const animateInput = motion.stage === 'reveal';

  const updateScrollButton = useCallback(() => {
    const remaining =
      scrollSize.current.content - scrollSize.current.viewport - previousScrollY.current;
    setShowScrollToBottom(!pinned.current && remaining > 80);
  }, []);

  const scrollToBottom = useCallback((force = false) => {
    if (force) {
      pinned.current = true;
      setShowScrollToBottom(false);
    }
    if (!pinned.current) return;
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    // Wait for the new question and its replies to finish their layout.
    scrollTimer.current = setTimeout(() => {
      scroll.current?.scrollToEnd({ animated: true });
      scrollTimer.current = null;
    }, 0);
  }, []);

  useEffect(
    () => () => {
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
    },
    [],
  );

  useEffect(() => {
    setContinueQuestions(false);
    setDislike(null);
    setShowScrollToBottom(false);
    lastRound.current = undefined;
    roundPositions.current = {};
    pinned.current = true;
    previousScrollY.current = 0;
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
  }, [advisor.storageKey, advisor.activeHistoryId]);

  useEffect(() => {
    if (inputMode && inputMode !== 'results') scrollToBottom(true);
  }, [inputKey, inputMode, motion.stage, scrollToBottom]);

  useEffect(() => {
    if (!latestRound || latestRound.id === lastRound.current) return;
    lastRound.current = latestRound.id;
    setDislike(null);
    setContinueQuestions(false);
    pinned.current = false;
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    const timer = setTimeout(() => {
      const y = roundPositions.current[latestRound.id];
      if (y !== undefined) scroll.current?.scrollTo({ y, animated: true });
      updateScrollButton();
    }, 100);
    return () => clearTimeout(timer);
  }, [latestRound?.id, updateScrollButton]);

  function follow(action: () => void) {
    pinned.current = true;
    setShowScrollToBottom(false);
    motion.begin();
    action();
  }

  function newRound() {
    motion.reset();
    advisor.reset();
    setDislike(null);
    setShowScrollToBottom(false);
    setContinueQuestions(false);
    lastRound.current = undefined;
    roundPositions.current = {};
    pinned.current = true;
    previousScrollY.current = 0;
  }

  function renderMatch(card: AdvisorCard, key: string) {
    return (
      <Match
        key={key}
        card={card}
        copy={copy}
        busy={busy || !!pendingConflict}
        readOnly={advisor.sessionExpired}
        reaction={
          advisor.session?.feedback.find((item) => item.breed_id === card.breed.id)?.reaction
        }
        onLike={() =>
          follow(() => {
            void advisor.refine({ breed_id: card.breed.id, reaction: 'like' });
          })
        }
        onDislike={() => follow(() => setDislike(card))}
      />
    );
  }

  function renderRound(round: AdvisorRound) {
    const previousChoices = round.favorites.filter((card) => card.is_favorite);
    const favorites = round.kind === 'more' ? [] : previousChoices;
    const favoriteIds = new Set(previousChoices.map((card) => card.breed.id));
    const candidates =
      round.kind === 'initial'
        ? [...round.cards, ...round.favorites.filter((card) => !card.is_favorite)]
        : round.cards;
    const matches = Array.from(
      new Map(candidates.map((card) => [card.breed.id, card])).values(),
    ).filter((card) => !favoriteIds.has(card.breed.id));
    const feedbackCard =
      round.feedback &&
      advisor.rounds
        .flatMap((item) => [...item.cards, ...item.favorites])
        .find((card) => card.breed.id === round.feedback?.breed_id);
    return (
      <View
        key={round.id}
        testID={`advisor-round-${round.id}`}
        className="gap-5"
        onLayout={(event) => {
          roundPositions.current[round.id] = event.nativeEvent.layout.y;
        }}
      >
        {round.text && <Sent>{round.text}</Sent>}
        {round.feedback && feedbackCard && (
          <Sent>{`${feedbackCard.breed.name}: ${copy(round.feedback.reaction === 'like' ? 'liked' : (round.feedback.reason ?? 'disliked'))}`}</Sent>
        )}
        {round.resolution && (
          <Sent>{copy(round.resolution.choice === 'text' ? 'use_text' : 'keep_answer')}</Sent>
        )}
        {round.conflicts
          ?.filter(
            (conflict) =>
              !advisor.session?.conflicts.some((pending) => sameConflict(conflict, pending)),
          )
          .map((conflict) => (
            <View key={conflict.trait} className="gap-4">
              <Question text={copy('conflicts_title')} label={copy('about_question')} />
              <Text className="text-brand-brown-dark text-sm leading-6">{`${conflict.answer}\n\n“${conflict.text}”`}</Text>
            </View>
          ))}
        <Question
          text={
            round.feedback?.reaction === 'like'
              ? copy('liked_confirmation')
              : copy(
                  favorites.length && matches.length ? 'results_intro_favorites' : 'results_intro',
                )
          }
          label={copy('about_question')}
        />
        {round.id === latestRound?.id && advisor.session?.descriptions_status === 'pending' && (
          <Text
            testID="advisor-descriptions-loading"
            accessibilityLiveRegion="polite"
            className="text-brand-brown-dark/60 text-xs leading-5"
          >
            {copy('descriptions_loading')}
          </Text>
        )}
        {!!favorites.length && (
          <BreedAdvisorResultGroup
            cards={favorites}
            title={copy('results_favorites_title')}
            favorite
            copy={copy}
            renderCard={(card) => renderMatch(card, `${round.id}-favorite-${card.breed.id}`)}
          />
        )}
        {!!matches.length && (
          <BreedAdvisorResultGroup
            cards={matches}
            title={copy('results_matches_title')}
            copy={copy}
            renderCard={(card) => renderMatch(card, `${round.id}-${card.breed.id}`)}
          />
        )}
        {round.id === latestRound?.id &&
          inputMode === 'results' &&
          !!advisor.session &&
          !pendingConflict && (
            <BreedAdvisorShare
              key={round.id}
              createShare={advisor.createShare}
              copy={copy}
              compact
            />
          )}
      </View>
    );
  }

  return (
    <View
      className="sentry-block relative h-full min-h-0 flex-1 overflow-hidden bg-white"
      testID="breed-advisor-conversation"
    >
      <View className="border-brand-brown-dark/10 flex-row items-center gap-2 border-b px-4 py-4">
        <View className="min-w-0 flex-1 gap-2">
          <Text className="text-brand-brown-dark text-left text-xl font-bold lg:text-2xl">
            {copy('title')}
          </Text>
          <View
            testID="advisor-progress-row"
            aria-hidden={!advisor.hydrated}
            className={`flex-row items-center gap-3 ${advisor.hydrated ? '' : 'opacity-0'}`}
          >
            <Text className="text-brand-brown-dark/60 shrink-0 text-left text-xs">
              {copy('coverage_count', coverageValues)}
            </Text>
            <View
              testID="advisor-progress"
              accessibilityRole="progressbar"
              accessibilityLabel={copy('coverage_description_flexible', coverageValues)}
              accessibilityValue={{
                min: 0,
                max: coverageValues.total,
                now: coverageValues.answered,
              }}
              aria-valuemin={0}
              aria-valuemax={coverageValues.total}
              aria-valuenow={coverageValues.answered}
              className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-stone-200"
            >
              <View
                testID="advisor-progress-fill"
                className="bg-brand-green h-full rounded-full"
                style={{
                  width: `${(coverageValues.answered / Math.max(1, coverageValues.total)) * 100}%`,
                }}
              />
            </View>
          </View>
        </View>
        <ConfirmModal
          title={copy('new_round_title')}
          description={copy('new_round_description')}
          confirmLabel={copy('new_round')}
          onConfirm={async () => newRound()}
          trigger={({ open }) => (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={copy('new_round')}
              testID="advisor-reset"
              disabled={!advisor.hydrated}
              className="border-brand-brown-dark/15 ml-2 size-9 shrink-0 items-center justify-center rounded border bg-white sm:ml-4"
              onPress={open}
            >
              <Icon icon={falArrowRotateLeft} className="text-brand-brown-dark/70 size-4" />
            </Pressable>
          )}
        />
      </View>
      <ScrollView
        ref={scroll}
        testID="advisor-conversation-scroll"
        className="min-h-0 flex-1"
        contentContainerClassName="grow justify-end gap-6 p-4"
        keyboardShouldPersistTaps="handled"
        onScroll={({ nativeEvent: e }) => {
          const nearBottom =
            e.contentSize.height - e.layoutMeasurement.height - e.contentOffset.y < 80;
          if (nearBottom) pinned.current = true;
          else if (e.contentOffset.y < previousScrollY.current - 1) pinned.current = false;
          previousScrollY.current = e.contentOffset.y;
          scrollSize.current = {
            content: e.contentSize.height,
            viewport: e.layoutMeasurement.height,
          };
          updateScrollButton();
        }}
        scrollEventThrottle={16}
        onLayout={({ nativeEvent }) => {
          scrollSize.current.viewport = nativeEvent.layout.height;
          updateScrollButton();
          scrollToBottom();
        }}
        onContentSizeChange={(_width, height) => {
          scrollSize.current.content = height;
          updateScrollButton();
          scrollToBottom();
        }}
      >
        <Text className="text-brand-brown-dark/65 max-w-[82%] text-base leading-6">
          {copy('intro')}
        </Text>
        {(started || advisor.favoriteStage === 'select') && (
          <Question
            text={copy('favorites_interest_question')}
            explanation={copy('favorites_description')}
            label={copy('about_question')}
          />
        )}
        {(started || advisor.favoriteStage === 'select') && (
          <Sent>{copy(advisor.favoriteInterest ? 'favorites_yes' : 'favorites_no')}</Sent>
        )}
        {started && advisor.favoriteInterest && (
          <Question
            text={copy('favorites_select_question')}
            explanation={copy('favorites_select_description')}
            label={copy('about_question')}
          />
        )}
        {started && advisor.favoriteInterest && (
          <Sent>
            {advisor.favoriteIds
              .map(
                (id) =>
                  breeds.find((breed) => breed.id === id)?.name ??
                  advisor.session?.favorites.find((card) => card.breed.id === id)?.breed.name,
              )
              .filter(Boolean)
              .join(', ') ||
              copy(
                advisor.favoriteIds.length ? 'favorites_continue' : 'favorites_selected_continue',
              )}
          </Sent>
        )}
        {advisor.answers.flatMap((answer, index) => {
          const question = advisor.questions.find((item) => item.id === answer.questionId);
          return [
            <View key={answer.questionId} className="gap-6">
              <Question
                text={answer.question}
                explanation={question?.explanation}
                label={copy('about_question')}
              />
              <Sent animated={motion.stage === 'typing' && index === advisor.answers.length - 1}>
                {answer.answer}
              </Sent>
            </View>,
            ...advisor.rounds.filter((round) => round.answerCount === index + 1).map(renderRound),
          ];
        })}
        {advisor.error && (
          <Text accessibilityRole="alert" className="text-brand-brown-dark text-sm leading-6">
            {advisor.error}
          </Text>
        )}
        {advisor.session?.ai_status === 'unavailable' && !busy && (
          <Text className="text-brand-brown-dark/65 text-xs leading-5">
            {copy('ai_unavailable')}
          </Text>
        )}
        {advisor.session?.descriptions_status === 'unavailable' && !busy && (
          <Text
            accessibilityLiveRegion="polite"
            className="text-brand-brown-dark/65 text-xs leading-5"
          >
            {copy('descriptions_unavailable')}
          </Text>
        )}
        {inputMode === 'results' && !advisor.session?.has_more && (
          <Text className="text-brand-brown-dark/65 text-sm leading-6">{copy('no_more')}</Text>
        )}
        {advisor.sessionExpired && (
          <View testID="advisor-history-expired" className="items-start gap-3">
            <Text className="text-brand-brown-dark/65 text-sm leading-6">
              {copy('history_expired')}
            </Text>
            <Button dense color="white" text={copy('new_round')} onPress={newRound} />
          </View>
        )}
        {inputMode && (
          <View
            key={`${inputMode}:${advisor.question?.id ?? ''}:${pendingConflict?.trait ?? ''}:${dislike?.breed.id ?? ''}`}
            testID="advisor-input-area"
            className="gap-3 bg-white"
            onLayout={() => scrollToBottom()}
          >
            {motion.stage === 'typing' && (
              <View testID="advisor-typing">
                <TypingIndicator name={copy('title')} padded={false} />
              </View>
            )}
            {showInput && (
              <AdvisorEntrance enabled={animateInput} duration={380}>
                <View className="gap-3">
                  {inputMode === 'favorites' && (
                    <>
                      <Question
                        text={copy('favorites_interest_question')}
                        explanation={copy('favorites_description')}
                        label={copy('about_question')}
                      />
                      <Replies>
                        <Reply
                          testID="advisor-favorites-yes"
                          onPress={() => follow(() => advisor.chooseFavorites(true))}
                        >
                          {copy('favorites_yes')}
                        </Reply>
                        <Reply
                          testID="advisor-favorites-no"
                          onPress={() => follow(() => advisor.chooseFavorites(false))}
                        >
                          {copy('favorites_no')}
                        </Reply>
                      </Replies>
                    </>
                  )}
                  {inputMode === 'favorite_select' && (
                    <>
                      <Question
                        text={copy('favorites_select_question')}
                        explanation={copy('favorites_select_description')}
                        label={copy('about_question')}
                      />
                      <View testID="advisor-favorites-picker" className="gap-3">
                        <BreedMultiSelect
                          animal={Animal.Dog}
                          value={advisor.favoriteIds}
                          maxSelections={advisorMaxFavorites}
                          allowQuery={false}
                          onChange={(ids) =>
                            advisor.setFavoriteIds(
                              ids.filter((id): id is number => typeof id === 'number'),
                            )
                          }
                        />
                        <Replies>
                          <Button
                            dense
                            color="white"
                            className="min-h-11 self-end"
                            textClassName="text-black"
                            text={copy('favorites_selected_continue')}
                            icon={falArrowRight}
                            testID="advisor-favorites-continue"
                            onPress={() => follow(advisor.finishFavorites)}
                          />
                        </Replies>
                      </View>
                    </>
                  )}
                  {inputMode === 'question' && advisor.question && (
                    <>
                      <View testID={`advisor-question-${advisor.question.id}`}>
                        <Question
                          text={advisor.question.text}
                          explanation={advisor.question.explanation}
                          label={copy('about_question')}
                        />
                      </View>
                      {advisor.question.id === 'size' ? (
                        <BreedAdvisorSizeInput
                          copy={copy}
                          onSubmit={(minimum, maximum) =>
                            follow(() => advisor.answerSizeRange(minimum, maximum))
                          }
                        />
                      ) : (
                        <Replies>
                          {advisor.question.options.map((option) => (
                            <Reply
                              key={option.value}
                              testID={`advisor-answer-${option.value}`}
                              onPress={() => follow(() => advisor.chooseAnswer(option.value))}
                            >
                              {option.label}
                            </Reply>
                          ))}
                        </Replies>
                      )}
                    </>
                  )}
                  {inputMode === 'text' && (
                    <>
                      <Question
                        text={copy('free_text_question')}
                        explanation={copy('free_text_description')}
                        label={copy('about_question')}
                      />
                      <View className="gap-3">
                        <TextInput
                          dense
                          multiline
                          value={advisor.freeText}
                          onChangeText={advisor.setFreeText}
                          maxLength={advisorMaxFreeTextLength}
                          accessibilityLabel={copy('free_text_question')}
                          placeholder={copy('free_text_placeholder')}
                          className="min-h-28 text-sm"
                        />
                        <Replies>
                          <Button
                            dense
                            color="white"
                            className="min-h-11 self-end"
                            textClassName="text-black"
                            text={copy('show_matches')}
                            icon={falArrowRight}
                            testID="advisor-show-matches"
                            onPress={() =>
                              follow(() => {
                                void advisor.submit();
                              })
                            }
                          />
                        </Replies>
                      </View>
                    </>
                  )}
                  {inputMode === 'conflict' && pendingConflict && (
                    <>
                      <Question text={copy('conflicts_title')} label={copy('about_question')} />
                      <Text className="text-brand-brown-dark text-sm leading-6">{`${pendingConflict.answer}\n\n“${pendingConflict.text}”`}</Text>
                      <Replies>
                        <Reply
                          onPress={() =>
                            follow(() => {
                              void advisor.resolveConflict(pendingConflict.trait, 'answer');
                            })
                          }
                        >
                          {copy('keep_answer')}
                        </Reply>
                        <Reply
                          onPress={() =>
                            follow(() => {
                              void advisor.resolveConflict(pendingConflict.trait, 'text');
                            })
                          }
                        >
                          {copy('use_text')}
                        </Reply>
                      </Replies>
                    </>
                  )}
                  {inputMode === 'dislike' && dislike && (
                    <>
                      <Sent>{`${dislike.breed.name}: ${copy('disliked')}`}</Sent>
                      <Question text={copy('why_dislike')} label={copy('about_question')} />
                      <Replies>
                        {feedbackReasons.map((reason) => (
                          <Reply
                            key={reason}
                            onPress={() =>
                              follow(() => {
                                void advisor.refine({
                                  breed_id: dislike.breed.id,
                                  reaction: 'dislike',
                                  reason,
                                });
                              })
                            }
                          >
                            {copy(reason)}
                          </Reply>
                        ))}
                        <Reply onPress={() => setDislike(null)}>{translate('common:cancel')}</Reply>
                      </Replies>
                    </>
                  )}
                  {inputMode === 'loading' && (
                    <View accessibilityLiveRegion="polite" testID="advisor-loading">
                      <TypingIndicator name={copy('title')} padded={false} />
                    </View>
                  )}
                  {inputMode === 'error' && (
                    <Replies>
                      <Reply
                        onPress={() => {
                          void advisor.retry();
                        }}
                      >
                        {copy('retry')}
                      </Reply>
                    </Replies>
                  )}
                  {inputMode === 'results' && (
                    <>
                      <Replies>
                        {advisor.session?.has_more && (
                          <Reply
                            testID="advisor-more"
                            onPress={() =>
                              follow(() => {
                                void advisor.more();
                              })
                            }
                          >
                            {copy('more')}
                          </Reply>
                        )}
                        {advisor.question && (
                          <Reply onPress={() => follow(() => setContinueQuestions(true))}>
                            {copy('continue_questions')}
                          </Reply>
                        )}
                        {advisor.session?.ai_status === 'unavailable' && (
                          <Reply
                            onPress={() => {
                              void advisor.retryAi();
                            }}
                          >
                            {copy('retry_interpretation')}
                          </Reply>
                        )}
                        {advisor.session?.descriptions_status === 'unavailable' && (
                          <Reply
                            onPress={() => {
                              void advisor.retryDescriptions();
                            }}
                          >
                            {copy('retry_descriptions')}
                          </Reply>
                        )}
                      </Replies>
                    </>
                  )}
                </View>
              </AdvisorEntrance>
            )}
          </View>
        )}
      </ScrollView>
      {showScrollToBottom && (
        <View pointerEvents="box-none" className="absolute inset-x-0 bottom-4 items-center">
          <Button
            color="white"
            dense
            center
            icon={falAngleDown}
            accessibilityLabel={copy('scroll_to_bottom')}
            testID="advisor-scroll-to-bottom"
            className="shadow-brand-brown-dark/10 size-11 !rounded-full !p-0 shadow-sm"
            onPress={() => scrollToBottom(true)}
          />
        </View>
      )}
    </View>
  );
}
